import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import settingsService from "./settingsService";

export const getMySettings = createAsyncThunk(
  "settings/getMySettings",
  async (_, thunkAPI) => {
    try {
      return await settingsService.getMySettings();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const updateProfile = createAsyncThunk(
  "settings/updateProfile",
  async (payload, thunkAPI) => {
    try {
      return await settingsService.updateProfile(payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const changePassword = createAsyncThunk(
  "settings/changePassword",
  async (payload, thunkAPI) => {
    try {
      return await settingsService.changePassword(payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const getSessions = createAsyncThunk(
  "settings/getSessions",
  async (_, thunkAPI) => {
    try {
      return await settingsService.getSessions();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const revokeSession = createAsyncThunk(
  "settings/revokeSession",
  async (id, thunkAPI) => {
    try {
      return await settingsService.revokeSession(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const revokeAllSessions = createAsyncThunk(
  "settings/revokeAllSessions",
  async (_, thunkAPI) => {
    try {
      return await settingsService.revokeAllSessions();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const startMfaReconfigure = createAsyncThunk(
  "settings/startMfaReconfigure",
  async (payload, thunkAPI) => {
    try {
      return await settingsService.startMfaReconfigure(payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const verifyMfaReconfigure = createAsyncThunk(
  "settings/verifyMfaReconfigure",
  async (payload, thunkAPI) => {
    try {
      return await settingsService.verifyMfaReconfigure(payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

const initialState = {
  user: null,
  sessions: [],
  mfaSetup: null,

  isLoading: false,
  isSavingProfile: false,
  isChangingPassword: false,
  isLoadingSessions: false,
  isRevokingSession: false,
  isReconfiguringMfa: false,

  error: null,
  success: null,
};

const settingsSlice = createSlice({
  name: "settings",

  initialState,

  reducers: {
    clearSettingsMessages: (state) => {
      state.error = null;
      state.success = null;
    },

    clearMfaSetup: (state) => {
      state.mfaSetup = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getMySettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getMySettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload?.data || null;
      })

      .addCase(getMySettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Instellingen laden mislukt.";
      })

      .addCase(updateProfile.pending, (state) => {
        state.isSavingProfile = true;
        state.error = null;
        state.success = null;
      })

      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isSavingProfile = false;
        state.user = action.payload?.data || state.user;
        state.success = "Profiel succesvol bijgewerkt.";
      })

      .addCase(updateProfile.rejected, (state, action) => {
        state.isSavingProfile = false;
        state.error = action.payload || "Profiel bijwerken mislukt.";
      })

      .addCase(changePassword.pending, (state) => {
        state.isChangingPassword = true;
        state.error = null;
        state.success = null;
      })

      .addCase(changePassword.fulfilled, (state) => {
        state.isChangingPassword = false;
        state.success = "Wachtwoord succesvol gewijzigd.";
      })

      .addCase(changePassword.rejected, (state, action) => {
        state.isChangingPassword = false;
        state.error = action.payload || "Wachtwoord wijzigen mislukt.";
      })

      .addCase(getSessions.pending, (state) => {
        state.isLoadingSessions = true;
        state.error = null;
      })

      .addCase(getSessions.fulfilled, (state, action) => {
        state.isLoadingSessions = false;
        state.sessions = action.payload?.data || [];
      })

      .addCase(getSessions.rejected, (state, action) => {
        state.isLoadingSessions = false;
        state.error = action.payload || "Sessies laden mislukt.";
      })

      .addCase(revokeSession.pending, (state) => {
        state.isRevokingSession = true;
        state.error = null;
      })

      .addCase(revokeSession.fulfilled, (state, action) => {
        state.isRevokingSession = false;
        const revokedId = action.meta.arg;
        state.sessions = state.sessions.filter(
          (session) => session._id !== revokedId,
        );
        state.success = "Sessie ingetrokken.";
      })

      .addCase(revokeSession.rejected, (state, action) => {
        state.isRevokingSession = false;
        state.error = action.payload || "Sessie intrekken mislukt.";
      })

      .addCase(revokeAllSessions.pending, (state) => {
        state.isRevokingSession = true;
        state.error = null;
      })

      .addCase(revokeAllSessions.fulfilled, (state) => {
        state.isRevokingSession = false;
        state.sessions = [];
        state.success = "Alle sessies zijn ingetrokken.";
      })

      .addCase(revokeAllSessions.rejected, (state, action) => {
        state.isRevokingSession = false;
        state.error = action.payload || "Sessies intrekken mislukt.";
      })

      .addCase(startMfaReconfigure.pending, (state) => {
        state.isReconfiguringMfa = true;
        state.error = null;
        state.success = null;
      })

      .addCase(startMfaReconfigure.fulfilled, (state, action) => {
        state.isReconfiguringMfa = false;
        state.mfaSetup = action.payload?.data || null;
        state.success = "Nieuwe MFA configuratie gestart.";
      })

      .addCase(startMfaReconfigure.rejected, (state, action) => {
        state.isReconfiguringMfa = false;
        state.error = action.payload || "MFA herconfiguratie starten mislukt.";
      })

      .addCase(verifyMfaReconfigure.pending, (state) => {
        state.isReconfiguringMfa = true;
        state.error = null;
      })

      .addCase(verifyMfaReconfigure.fulfilled, (state) => {
        state.isReconfiguringMfa = false;
        state.success = "MFA succesvol gewijzigd. Log opnieuw in.";
      })

      .addCase(verifyMfaReconfigure.rejected, (state, action) => {
        state.isReconfiguringMfa = false;
        state.error = action.payload || "MFA verificatie mislukt.";
      });
  },
});

export const { clearSettingsMessages, clearMfaSetup } = settingsSlice.actions;

export default settingsSlice.reducer;
